// Validation Analysis Hooks
// React Query hooks for startup validation ladder analysis

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useProjectStore } from '@/stores/projectStore'
import { useAuthStore } from '@/stores/authStore'
import {
  validationAnalysisApi,
  type ValidationAnalysisResponse,
  type ValidationStageResponse,
  type ValidationStatusResponse,
} from '@/lib/api/client'
import type { ValidationStageKey } from '@ispot/shared'

// ============================================================================
// QUERY KEYS
// ============================================================================

export const validationAnalysisKeys = {
  all: ['validationAnalysis'] as const,
  stages: () => [...validationAnalysisKeys.all, 'stages'] as const,
  analyses: (projectId: string) => [...validationAnalysisKeys.all, 'analyses', projectId] as const,
  analysis: (projectId: string, analysisId: string) =>
    [...validationAnalysisKeys.all, 'analysis', projectId, analysisId] as const,
  latest: (projectId: string) => [...validationAnalysisKeys.all, 'latest', projectId] as const,
  latestCompleted: (projectId: string) =>
    [...validationAnalysisKeys.all, 'latestCompleted', projectId] as const,
  status: (projectId: string, analysisId: string) =>
    [...validationAnalysisKeys.all, 'status', projectId, analysisId] as const,
}

// ============================================================================
// STAGE HOOKS (Reference Data)
// ============================================================================

/**
 * Get all validation stages (reference data)
 */
export function useValidationStages() {
  const currentProject = useProjectStore((state) => state.currentProject)
  const currentAccount = useAuthStore((state) => state.currentAccount)

  return useQuery({
    queryKey: validationAnalysisKeys.stages(),
    queryFn: async () => {
      if (!currentAccount?.id || !currentProject?.id) {
        throw new Error('No account or project selected')
      }
      const response = await validationAnalysisApi.getStages(currentAccount.id, currentProject.id)
      return response.stages
    },
    enabled: !!currentAccount?.id && !!currentProject?.id,
    staleTime: Infinity, // Stages are reference data, rarely change
  })
}

/**
 * Get a single validation stage by key
 */
export function useValidationStage(stageKey: ValidationStageKey | null) {
  const currentProject = useProjectStore((state) => state.currentProject)
  const currentAccount = useAuthStore((state) => state.currentAccount)

  return useQuery({
    queryKey: [...validationAnalysisKeys.stages(), stageKey],
    queryFn: async () => {
      if (!currentAccount?.id || !currentProject?.id || !stageKey) {
        throw new Error('Missing required parameters')
      }
      const response = await validationAnalysisApi.getStage(currentAccount.id, currentProject.id, stageKey)
      return response.stage
    },
    enabled: !!currentAccount?.id && !!currentProject?.id && !!stageKey,
    staleTime: Infinity,
  })
}

// ============================================================================
// ANALYSIS HOOKS
// ============================================================================

/**
 * List all validation analyses for the current project
 */
export function useValidationAnalyses() {
  const currentProject = useProjectStore((state) => state.currentProject)
  const currentAccount = useAuthStore((state) => state.currentAccount)

  return useQuery({
    queryKey: validationAnalysisKeys.analyses(currentProject?.id ?? ''),
    queryFn: async () => {
      if (!currentAccount?.id || !currentProject?.id) {
        throw new Error('No account or project selected')
      }
      return validationAnalysisApi.list(currentAccount.id, currentProject.id)
    },
    enabled: !!currentAccount?.id && !!currentProject?.id,
  })
}

/**
 * Get the latest validation analysis for the current project
 */
export function useLatestValidationAnalysis() {
  const currentProject = useProjectStore((state) => state.currentProject)
  const currentAccount = useAuthStore((state) => state.currentAccount)

  return useQuery({
    queryKey: validationAnalysisKeys.latest(currentProject?.id ?? ''),
    queryFn: async () => {
      if (!currentAccount?.id || !currentProject?.id) {
        throw new Error('No account or project selected')
      }
      try {
        const response = await validationAnalysisApi.getLatest(currentAccount.id, currentProject.id)
        return response.analysis
      } catch (error: unknown) {
        // Return null if no analysis found (404)
        if (error instanceof Error && 'status' in error && (error as { status: number }).status === 404) {
          return null
        }
        throw error
      }
    },
    enabled: !!currentAccount?.id && !!currentProject?.id,
  })
}

/**
 * Get the latest completed validation analysis
 */
export function useLatestCompletedAnalysis() {
  const currentProject = useProjectStore((state) => state.currentProject)
  const currentAccount = useAuthStore((state) => state.currentAccount)

  return useQuery({
    queryKey: validationAnalysisKeys.latestCompleted(currentProject?.id ?? ''),
    queryFn: async () => {
      if (!currentAccount?.id || !currentProject?.id) {
        throw new Error('No account or project selected')
      }
      try {
        const response = await validationAnalysisApi.getLatestCompleted(currentAccount.id, currentProject.id)
        return response.analysis
      } catch (error: unknown) {
        if (error instanceof Error && 'status' in error && (error as { status: number }).status === 404) {
          return null
        }
        throw error
      }
    },
    enabled: !!currentAccount?.id && !!currentProject?.id,
  })
}

/**
 * Get a specific validation analysis by ID
 */
export function useValidationAnalysis(analysisId: string | null) {
  const currentProject = useProjectStore((state) => state.currentProject)
  const currentAccount = useAuthStore((state) => state.currentAccount)

  return useQuery({
    queryKey: validationAnalysisKeys.analysis(currentProject?.id ?? '', analysisId ?? ''),
    queryFn: async () => {
      if (!currentAccount?.id || !currentProject?.id || !analysisId) {
        throw new Error('Missing required parameters')
      }
      const response = await validationAnalysisApi.get(currentAccount.id, currentProject.id, analysisId)
      return response.analysis
    },
    enabled: !!currentAccount?.id && !!currentProject?.id && !!analysisId,
  })
}

/**
 * Poll analysis status (for running analyses)
 */
export function useValidationAnalysisStatus(
  analysisId: string | null,
  options?: { enabled?: boolean; refetchInterval?: number | ((data: ValidationStatusResponse | undefined) => number | false) }
) {
  const currentProject = useProjectStore((state) => state.currentProject)
  const currentAccount = useAuthStore((state) => state.currentAccount)

  return useQuery({
    queryKey: validationAnalysisKeys.status(currentProject?.id ?? '', analysisId ?? ''),
    queryFn: async () => {
      if (!currentAccount?.id || !currentProject?.id || !analysisId) {
        throw new Error('Missing required parameters')
      }
      const response = await validationAnalysisApi.getStatus(currentAccount.id, currentProject.id, analysisId)
      return response.status
    },
    enabled: (options?.enabled ?? true) && !!currentAccount?.id && !!currentProject?.id && !!analysisId,
    refetchInterval: options?.refetchInterval ?? false,
  })
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Create a new validation analysis
 */
export function useCreateValidationAnalysis() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      // Get state at execution time, not hook initialization time
      const { currentAccount } = useAuthStore.getState()
      const { currentProject } = useProjectStore.getState()
      if (!currentAccount?.id || !currentProject?.id) {
        throw new Error('No account or project selected')
      }
      const response = await validationAnalysisApi.create(currentAccount.id, currentProject.id)
      return response.analysis
    },
    onSuccess: () => {
      const { currentProject } = useProjectStore.getState()
      // Invalidate analyses list and latest
      queryClient.invalidateQueries({
        queryKey: validationAnalysisKeys.analyses(currentProject?.id ?? ''),
      })
      queryClient.invalidateQueries({
        queryKey: validationAnalysisKeys.latest(currentProject?.id ?? ''),
      })
    },
  })
}

/**
 * Run a pending validation analysis
 */
export function useRunValidationAnalysis() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (analysisId: string) => {
      // Get state at execution time, not hook initialization time
      const { currentAccount } = useAuthStore.getState()
      const { currentProject } = useProjectStore.getState()
      if (!currentAccount?.id || !currentProject?.id) {
        throw new Error('No account or project selected')
      }
      const response = await validationAnalysisApi.run(currentAccount.id, currentProject.id, analysisId)
      return response.analysis
    },
    onSuccess: (_, analysisId) => {
      const { currentProject } = useProjectStore.getState()
      // Invalidate this analysis and related queries
      queryClient.invalidateQueries({
        queryKey: validationAnalysisKeys.analysis(currentProject?.id ?? '', analysisId),
      })
      queryClient.invalidateQueries({
        queryKey: validationAnalysisKeys.status(currentProject?.id ?? '', analysisId),
      })
      queryClient.invalidateQueries({
        queryKey: validationAnalysisKeys.latest(currentProject?.id ?? ''),
      })
    },
  })
}

/**
 * Delete a validation analysis
 */
export function useDeleteValidationAnalysis() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (analysisId: string) => {
      // Get state at execution time, not hook initialization time
      const { currentAccount } = useAuthStore.getState()
      const { currentProject } = useProjectStore.getState()
      if (!currentAccount?.id || !currentProject?.id) {
        throw new Error('No account or project selected')
      }
      await validationAnalysisApi.delete(currentAccount.id, currentProject.id, analysisId)
      return analysisId
    },
    onSuccess: (_, analysisId) => {
      const { currentProject } = useProjectStore.getState()
      // Invalidate all related queries
      queryClient.invalidateQueries({
        queryKey: validationAnalysisKeys.analyses(currentProject?.id ?? ''),
      })
      queryClient.invalidateQueries({
        queryKey: validationAnalysisKeys.analysis(currentProject?.id ?? '', analysisId),
      })
      queryClient.invalidateQueries({
        queryKey: validationAnalysisKeys.latest(currentProject?.id ?? ''),
      })
      queryClient.invalidateQueries({
        queryKey: validationAnalysisKeys.latestCompleted(currentProject?.id ?? ''),
      })
    },
  })
}

// ============================================================================
// COMPOSITE HOOKS
// ============================================================================

/**
 * Create and run a new validation analysis in one action
 */
export function useStartValidationAnalysis() {
  const createMutation = useCreateValidationAnalysis()
  const runMutation = useRunValidationAnalysis()

  return useMutation({
    mutationFn: async () => {
      // Create the analysis
      const analysis = await createMutation.mutateAsync()
      // Run it
      return runMutation.mutateAsync(analysis.id)
    },
  })
}

/**
 * Hook to poll a running analysis until completion
 */
export function usePollingValidationAnalysis(analysisId: string | null) {
  const { data: status } = useValidationAnalysisStatus(analysisId, {
    enabled: !!analysisId,
    refetchInterval: (data) => {
      // Stop polling if completed or failed
      if (!data) return 2000 // Poll every 2s while loading
      if (data.status === 'completed' || data.status === 'failed') {
        return false
      }
      return 2000 // Poll every 2s while running
    },
  })

  return {
    status,
    isRunning: status?.status === 'gathering' ||
               status?.status === 'evaluating' ||
               status?.status === 'generating',
    isComplete: status?.status === 'completed',
    isFailed: status?.status === 'failed',
    progress: status?.progress ?? { stepsCompleted: 0, totalSteps: 3, currentStepName: null },
  }
}

// Re-export types for convenience
export type { ValidationAnalysisResponse, ValidationStageResponse, ValidationStatusResponse }
